import {
  VOIP_STATS_INTERVAL_MS,
  VoipCall,
  VoipCallStats,
  VoipDiagnostics,
  VoipEventDetails,
  VoipPhase,
  classifyRoomDisconnect,
  flattenCallStats,
  getBrowserNetworkInfo,
  logVoipEvent,
} from './voipKibanaLogger';

type Listener = (...args: never[]) => void;

type Emitter = {
  off?: (event: string, fn: Listener) => void;
  on?: (event: string, fn: Listener) => void;
};

type TwilioParticipant = Emitter & { identity?: string };

type TwilioRoom = Emitter & {
  getStats?: () => Promise<Parameters<typeof flattenCallStats>[0]>;
  localParticipant?: TwilioParticipant;
  name?: string;
  once?: (event: string, fn: Listener) => void;
  participants?: { forEach?: (fn: (p: TwilioParticipant) => void) => void; size: number };
  sid?: string;
};

export type TelemetryCall = VoipCall & { room?: TwilioRoom | null };

export type VoipCallTelemetryDeps = {
  getCurrentUserId: () => string | undefined;
  isEnabled: () => boolean;
  isMetricsEnabled: () => boolean;
  send: Parameters<typeof logVoipEvent>[0]['send'];
};

type Session = {
  call: TelemetryCall;
  endLogged: boolean;
  lastNetworkQuality: number | null;
  lastStats?: VoipCallStats;
  listeners: Array<[Emitter, string, Listener]>;
  minNetworkQuality: number | null;
  onTabClose?: () => void;
  reconnectCount: number;
  room: TwilioRoom;
  startedAt: number;
  statsInFlight: boolean;
  statsInterval: ReturnType<typeof setInterval> | null;
};

// Owns VoIP Kibana logging for one call at a time: room listeners, the stats loop and the
// single call_end per call. Every public method is safe to call from call code; failures
// are swallowed so logging can never break a call.
export default class VoipCallTelemetry {
  private deps: VoipCallTelemetryDeps;
  private session: Session | null = null;

  constructor(deps: VoipCallTelemetryDeps) {
    this.deps = deps;
  }

  log(phase: VoipPhase, call?: TelemetryCall | null, details?: VoipEventDetails) {
    try {
      if (!this.deps.isEnabled()) return;
      logVoipEvent(
        { currentUserId: this.deps.getCurrentUserId(), send: this.deps.send },
        phase,
        call ?? this.session?.call,
        details
      );
    } catch (err) {
      console.error('VoipCallTelemetry.log failed', err);
    }
  }

  // Logs call_end once per call; the first source to report the end wins.
  logCallEnd(details: VoipEventDetails, call?: TelemetryCall | null, session = this.session) {
    try {
      if (session) {
        if (session.endLogged) return;
        session.endLogged = true;
      }
      const target = call ?? session?.call;
      this.log('call_end', target, {
        callEndCause: (target as { endCallState?: string } | null | undefined)?.endCallState,
        diagnostics: this.diagnostics(session),
        ...details,
      });
    } catch (err) {
      console.error('VoipCallTelemetry.logCallEnd failed', err);
    }
  }

  // Starts a session when the call has a room we are not already watching.
  sync(call: TelemetryCall | null | undefined) {
    try {
      if (!call) return this.detach();
      if (!this.deps.isEnabled()) return;
      const room = call.room;
      if (!room) {
        // A different call with no room yet (e.g. a new incoming call): stop tracking the old
        // one so its session isn't used for this call's events.
        if (this.session && this.session.call.roomName !== call.roomName) this.detach();
        return;
      }
      if (room === this.session?.room) return;
      this.detach();
      this.attach(call, room);
    } catch (err) {
      console.error('VoipCallTelemetry.sync failed', err);
    }
  }

  // Stops the stats loop and tab-close listener. Room listeners stay until the room
  // disconnects so a late disconnect is still logged against its own call.
  detach() {
    if (!this.session) return;
    this.stopTimers(this.session);
    this.session = null;
  }

  private attach(call: TelemetryCall, room: TwilioRoom) {
    const session: Session = {
      call,
      endLogged: false,
      lastNetworkQuality: null,
      listeners: [],
      minNetworkQuality: null,
      reconnectCount: 0,
      room,
      startedAt: Date.now(),
      statsInFlight: false,
      statsInterval: null,
    };
    this.session = session;

    const log = (phase: VoipPhase, details?: VoipEventDetails) => this.log(phase, call, details);
    const listen = (emitter: Emitter | undefined, event: string, fn: Listener) => {
      if (!emitter?.on) return;
      emitter.on(event, fn);
      session.listeners.push([emitter, event, fn]);
    };

    const watchRemoteQuality = (participant: TwilioParticipant) =>
      listen(participant, 'networkQualityLevelChanged', ((level: number) =>
        log('network_quality_changed', {
          networkQuality: level,
          participantId: participant.identity,
          remote: true,
        })) as Listener);
    room.participants?.forEach?.(watchRemoteQuality);

    listen(room, 'participantConnected', ((participant: TwilioParticipant) => {
      watchRemoteQuality(participant);
      log('participant_added', { participantId: participant?.identity });
    }) as Listener);
    listen(room, 'participantDisconnected', ((participant: TwilioParticipant) =>
      log('participant_removed', {
        lastParticipantLeft: room.participants?.size === 0,
        participantId: participant?.identity,
      })) as Listener);
    listen(room, 'reconnecting', ((error?: { code?: number }) => {
      session.reconnectCount += 1;
      log('reconnecting', { code: error?.code, diagnostics: this.diagnostics(session) });
    }) as Listener);
    listen(room, 'reconnected', (() => log('reconnected')) as Listener);

    const local = room.localParticipant;
    listen(local, 'networkQualityLevelChanged', ((level: number) => {
      session.lastNetworkQuality = level;
      if (session.minNetworkQuality === null || level < session.minNetworkQuality) {
        session.minNetworkQuality = level;
      }
      log('network_quality_changed', { networkQuality: level });
    }) as Listener);
    listen(local, 'trackDisabled', ((track?: { kind?: string }) => {
      if (track?.kind === 'audio') log('mute_toggled', { mute: true });
    }) as Listener);
    listen(local, 'trackEnabled', ((track?: { kind?: string }) => {
      if (track?.kind === 'audio') log('mute_toggled', { mute: false });
    }) as Listener);

    session.onTabClose = () =>
      this.logCallEnd({ endedBy: 'tab_closed', disconnectReason: 'beforeunload' }, call, session);
    window.addEventListener('beforeunload', session.onTabClose);

    room.once?.('disconnected', ((_: unknown, error?: { code?: number } | null) =>
      this.onRoomDisconnected(session, error)) as Listener);

    if (this.deps.isMetricsEnabled() && room.getStats) {
      session.statsInterval = setInterval(() => this.pollStats(session), VOIP_STATS_INTERVAL_MS);
    }
  }

  private onRoomDisconnected(session: Session, error?: { code?: number } | null) {
    try {
      this.stopTimers(session);
      session.listeners.forEach(([emitter, event, fn]) => emitter.off?.(event, fn));
      session.listeners = [];
      if (this.session === session) this.session = null;

      const { endedBy, disconnectReason } = classifyRoomDisconnect(error);
      // A completed room is a normal end; only unexpected errors are failures.
      if (!error || endedBy === 'room_completed') {
        this.logCallEnd({ code: error?.code, disconnectReason, endedBy }, session.call, session);
      } else if (!session.endLogged) {
        session.endLogged = true;
        this.log('failed', session.call, {
          code: error.code,
          diagnostics: this.diagnostics(session),
          disconnectReason,
          endedBy,
        });
      }
    } catch (err) {
      console.error('VoipCallTelemetry.onRoomDisconnected failed', err);
    }
  }

  private async pollStats(session: Session) {
    if (session.statsInFlight || !session.room.getStats) return;
    session.statsInFlight = true;
    try {
      const stats = flattenCallStats(await session.room.getStats());
      // The call may have ended while getStats was pending.
      if (!session.statsInterval || session.endLogged) return;
      session.lastStats = stats;
      this.log('call_stats', session.call, { stats });
    } catch (err) {
      console.error(err);
    } finally {
      session.statsInFlight = false;
    }
  }

  private stopTimers(session: Session) {
    if (session.statsInterval) {
      clearInterval(session.statsInterval);
      session.statsInterval = null;
    }
    if (session.onTabClose) {
      window.removeEventListener('beforeunload', session.onTabClose);
      session.onTabClose = undefined;
    }
  }

  private diagnostics(session: Session | null): VoipDiagnostics {
    return {
      ...getBrowserNetworkInfo(),
      callDuration: session ? Math.round((Date.now() - session.startedAt) / 1000) : undefined,
      lastNetworkQuality: session?.lastNetworkQuality,
      lastStats: session?.lastStats,
      minNetworkQuality: session?.minNetworkQuality,
      reconnectCount: session?.reconnectCount,
      remainingParticipants: session?.room.participants?.size,
    };
  }
}
